import type { VariantType as Variant } from 'motion-v'

/**
 * Brand-level hover motion variants. Apply via motion-v's `whileHover`,
 * either directly on a `<Motion>` component or through one of the
 * `MMotion{Wobble,Wiggle,Shake,Settle}` wrappers.
 *
 * Mirrored as `@keyframes mm-{name}` in `assets/css/main.css` for the
 * places we can't wrap a node (e.g. content-renderer output, sitewide
 * `.shake-trigger:hover .shake` selectors).
 */

const easeInOut = 'easeInOut'

export const wobble: Variant = {
  x: [0, -3, 3, -2, 2, -1, 0],
  rotate: [0, -2, 2, -1, 1, -0.4, 0],
  transition: { duration: 0.7, repeat: Infinity, ease: easeInOut },
}

export const wobbleHor: Variant = {
  x: [0, -5, 5, -3, 3, -2, 0],
  rotate: [0, -3, 3, -1, 1, -0.4, 0],
  scale: 1.2,
  transition: {
    x: { duration: 0.8, repeat: Infinity, ease: easeInOut },
    rotate: { duration: 0.8, repeat: Infinity, ease: easeInOut },
    scale: { duration: 0.25 },
  },
}

export const wiggle: Variant = {
  rotate: [0, 3, 0, -3, 0],
  filter: [
    'drop-shadow(0 0 5px rgb(255 0 0 / 0.5))',
    'drop-shadow(0 0 7px rgb(255 0 0 / 0.6))',
  ],
  transition: { duration: 1, repeat: Infinity, repeatType: 'reverse', ease: easeInOut },
}

export const shake: Variant = {
  x: [0, 1.5, -0.5, 2.5, 0.5, -1.5, 1.5, -1.5, 2.5, 0],
  y: [0, -1.5, 2.5, 0.5, -0.5, 1.5, 2.5, -0.5, 1.5, 0],
  rotate: [0, 1.5, -0.5, 1.5, -0.5, 0.5, -0.5, 1.5, 0.5, 0],
  transition: { duration: 0.4, repeat: Infinity, ease: 'linear' },
}

export const settle: Variant = {
  y: [-2, 1, 0],
  transition: { duration: 0.4, ease: easeInOut },
}

/** Generic card hover lift. Prefer `gameCardVariants` / `articleCardVariants` for branded surfaces. */
export const liftVariants = {
  rest: { y: 0, transition: { type: 'spring', stiffness: 320, damping: 26 } },
  lift: { y: -4, transition: { type: 'spring', stiffness: 360, damping: 16 } },
} as const

/**
 * Game cards: "Massive Adventures" pillar. Bigger, juicier lift with mild overshoot
 * and a sliver of rotate so the card reads as tactile paper. Pair with
 * `group-hover:shadow-monster-lg` + red glow on the rounded surface.
 */
export const gameCardVariants = {
  rest: {
    y: 0,
    rotate: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24, mass: 0.9 },
  },
  lift: {
    y: -8,
    rotate: -0.7,
    scale: 1.02,
    transition: { type: 'spring', stiffness: 340, damping: 13, mass: 0.9 },
  },
} as const

/**
 * Article cards: editorial, refined. Smaller lift, no rotate. Pair with
 * `group-hover:shadow-monster` + blue glow on the rounded surface.
 */
export const articleCardVariants = {
  rest: {
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 360, damping: 28, mass: 0.7 },
  },
  lift: {
    y: -3,
    scale: 1.008,
    transition: { type: 'spring', stiffness: 420, damping: 22, mass: 0.7 },
  },
} as const

/** Smaller hover lift — used on dense grids (brand-kit cards, etc.). */
export const liftSmallVariants = {
  rest: { y: 0 },
  lift: {
    y: -2,
    transition: { type: 'spring', stiffness: 420, damping: 24, mass: 0.7 },
  },
} as const

/**
 * Tile lift + play-icon scale. Variant labels propagate from parent to child:
 * parent gets `tileVariants`, child play-icon gets `playIconVariants`. Setting
 * parent's `whileHover="lift"` triggers both.
 */
export const tileVariants = {
  rest: { y: 0 },
  lift: {
    y: -3,
    transition: { type: 'spring', stiffness: 400, damping: 22, mass: 0.8 },
  },
} as const

export const playIconVariants = {
  rest: { scale: 1, opacity: 0.9 },
  lift: { scale: 1.25, opacity: 1, transition: { duration: 0.2 } },
} as const

/**
 * Heavier hover lift with red glow. Used on `cult-of-the-lamb` platform
 * link buttons, where the hover wants to read as "this is hot". Variant
 * label flows to child SVG to whiten its colour on the same hover.
 */
export const platformLinkVariants = {
  rest: {
    y: 0,
    boxShadow: '0 0 0 rgb(205 38 35 / 0)',
  },
  lift: {
    y: -5,
    boxShadow: '0 0 8px oklch(55% 0.182 27.5 / 0.5)',
    transition: { type: 'spring', stiffness: 360, damping: 20 },
  },
} as const

export const platformLinkSvgVariants = {
  rest: { color: 'oklch(70% 0 0)', fill: 'oklch(70% 0 0)' },
  lift: { color: '#fff', fill: '#fff', transition: { duration: 0.2 } },
} as const

export const brandMotion = { wobble, wobbleHor, wiggle, shake, settle } as const
export type BrandMotion = keyof typeof brandMotion
